import type { Config } from "tailwindcss";

import tailwindContainerQueriesPlugin from "@tailwindcss/container-queries";
import tailwindAnimatePlugin from "tailwindcss-animate";
import defaultTheme from "tailwindcss/defaultTheme";

import { dsColors, dsThemeColors } from "./colors";

const hex2rgb = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
};

export default {
  content: ["./**/*.{ts,tsx}"],
  darkMode: ["class"],
  prefix: "af-",
  theme: {
    container: {
      center: true,
      padding: "2rem",
      screens: {
        "2xl": "1400px",
      },
    },
    // Include font family classes in the generated CSS
    safelist: ["af-font-sans", "af-font-mono", "af-font-code"],
    extend: {
      colors: dsThemeColors,
      boxShadow: {
        sm: `0px 1px 2px 0px rgba(${hex2rgb(dsColors.blue[950])}, 0.05)`,
        DEFAULT: `0px 1px 3px 0px rgba(${hex2rgb(
          dsColors.blue[600],
        )}, 0.07), 0px 1px 2px 0px rgba(${hex2rgb(dsColors.blue[950])}, 0.05)`,
        md: `0px 4px 6px -1px rgba(${hex2rgb(
          dsColors.blue[600],
        )}, 0.07), 0px 2px 4px -1px rgba(${hex2rgb(dsColors.blue[950])}, 0.05)`,
        lg: `0px 10px 15px -3px rgba(${hex2rgb(
          dsColors.blue[600],
        )}, 0.07), 0px 4px 6px -2px rgba(${hex2rgb(dsColors.blue[950])}, 0.05)`,
        xl: `0px 20px 25px -5px rgba(${hex2rgb(
          dsColors.blue[600],
        )}, 0.07), 0px 10px 10px -5px rgba(${hex2rgb(
          dsColors.blue[950],
        )}, 0.05)`,
        "2xl": `0px 25px 50px -12px rgba(${hex2rgb(dsColors.blue[600])}, 0.15)`,
        inner: `0px 2px 4px 0px rgba(${hex2rgb(
          dsColors.blue[950],
        )}, 0.06) inset`,
      },
      fontFamily: {
        sans: `var(--af-font-sans, ${defaultTheme.fontFamily.sans.join(", ")})`,
        mono: `var(--af-font-mono, ${defaultTheme.fontFamily.mono.join(", ")})`,
        code: `var(--af-font-code, 'DM Mono', ${defaultTheme.fontFamily.mono.join(
          ", ",
        )})`,
      },
      keyframes: {
        "fade-in": {
          from: { opacity: "0" },
          to: { opacity: "1" },
        },
        "accordion-down": {
          from: { height: "0" },
          to: { height: "var(--radix-accordion-content-height)" },
        },
        "accordion-up": {
          from: { height: "var(--radix-accordion-content-height)" },
          to: { height: "0" },
        },
        "collapsible-down": {
          from: {
            height: "0",
          },
          to: {
            height: "var(--radix-collapsible-content-height)",
          },
        },
        "collapsible-up": {
          from: {
            height: "var(--radix-collapsible-content-height)",
          },
          to: {
            height: "0",
          },
        },
      },
      animation: {
        "fade-in": "fade-in 0.5s ease",
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
        "collapsible-down": "collapsible-down 0.2s ease-out",
        "collapsible-up": "collapsible-up 0.2s ease-out",
      },
    },
  },
  corePlugins: {
    preflight: false,
  },
  plugins: [tailwindAnimatePlugin, tailwindContainerQueriesPlugin],
} satisfies Config;
